import { useEffect, useState } from "react";
import { api, fmtBRL } from "@/lib/api";
import { CheckCircle, MagnifyingGlass, X, Trash } from "@phosphor-icons/react";

/**
 * Modal para editar os itens de um pedido (adicionar, remover, mudar qty,
 * preço, cor do silk). Extraído de OrderDetail.jsx.
 */
export default function ItemsEditor({ order, silkOptions, onSaved, onClose }) {
  const [items, setItems] = useState(() => (order.order_items || []).map((it) => ({
    sku: it.sku, name: it.name, qty: it.qty, unit_price: it.unit_price,
    units_per_unit: it.units_per_unit, type: it.type, cups: it.cups,
    silk_color: it.silk_color || "",
    color_breakdown: it.color_breakdown || [],
  })));
  const [productSearch, setProductSearch] = useState("");
  const [products, setProducts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const t = setTimeout(async () => {
      if (productSearch.length === 0) { setProducts([]); return; }
      try {
        const { data } = await api.get("/products", { params: { search: productSearch, only_active: true } });
        setProducts(data.slice(0, 12));
      } catch (e) {
        console.warn("Product search falhou:", e?.response?.status);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [productSearch]);

  const addProduct = (p) => {
    setItems((prev) => {
      const exists = prev.find((it) => it.sku === p.sku);
      if (exists) return prev.map((it) => it.sku === p.sku ? { ...it, qty: it.qty + 1 } : it);
      return [...prev, {
        sku: p.sku, name: p.name, qty: 1, unit_price: p.price || 0,
        units_per_unit: p.sku_parsed?.units_per_unit || 1,
        type: p.sku_parsed?.type || "Genérico",
        cups: (p.sku_parsed?.units_per_unit || 1),
        color_breakdown: [],
      }];
    });
    setProductSearch("");
  };

  const updateField = (i, k, v) => {
    const c = [...items];
    c[i] = { ...c[i], [k]: v };
    if (k === "qty") c[i].cups = (parseInt(v) || 0) * (c[i].units_per_unit || 1);
    setItems(c);
  };
  const removeItem = (i) => setItems(items.filter((_, j) => j !== i));

  const totalCups = items.reduce((s, it) => s + (parseInt(it.qty || 0) * (it.units_per_unit || 1)), 0);
  const totalValue = items.reduce((s, it) => s + (parseInt(it.qty || 0) * parseFloat(it.unit_price || 0)), 0);

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      if (items.length === 0) { setError("Adicione pelo menos 1 item"); setSaving(false); return; }
      const payload = { items: items.map((it) => ({ sku: it.sku, name: it.name, qty: parseInt(it.qty) || 0, unit_price: parseFloat(it.unit_price) || 0, silk_color: it.silk_color || null, color_breakdown: it.color_breakdown || [] })) };
      const { data } = await api.put(`/orders/${order.id}/items`, payload);
      onSaved(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="glass max-w-5xl w-full p-6 md:p-8 my-4" onClick={(e) => e.stopPropagation()} data-testid="items-editor-modal">
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="overline text-zinc-500">EDITAR ITENS</div>
            <h2 className="font-head text-2xl font-black">Pedido #{order.bling_number}</h2>
          </div>
          <button onClick={onClose}><X size={22} /></button>
        </div>

        <div className="border border-black p-4 bg-white mb-4">
          <div className="overline mb-2">ADICIONAR PRODUTO</div>
          <div className="relative">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input className="input pl-10" placeholder="Buscar SKU ou nome no catálogo" value={productSearch} onChange={(e) => setProductSearch(e.target.value)} data-testid="ed-product-search" />
          </div>
          {products.length > 0 && (
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2 max-h-52 overflow-y-auto">
              {products.map((p) => (
                <button type="button" key={p.id} className="border border-zinc-200 hover:border-black p-2 text-left flex items-center justify-between" onClick={() => addProduct(p)}>
                  <div className="min-w-0">
                    <div className="font-mono font-bold text-sm">{p.sku}</div>
                    <div className="text-xs text-zinc-600 truncate">{p.name}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-sm">{fmtBRL(p.price)}</div>
                    <div className="text-[10px] text-[#002FA7]">{p.sku_parsed?.units_per_unit} un</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="border border-black bg-white mb-4">
          <table className="table">
            <thead><tr><th>SKU</th><th>Produto</th><th>Tipo</th><th className="text-center">Qtd</th><th className="text-right">Preço Un</th><th className="text-right">Total</th><th className="text-center">Copos</th><th>Cor do Silk</th><th></th></tr></thead>
            <tbody>
              {items.length === 0 ? (
                <tr><td colSpan={9} className="text-center py-10 text-zinc-400">Adicione itens acima</td></tr>
              ) : items.map((it, i) => (
                <tr key={it.sku || `new-${i}`}>
                  <td className="font-mono font-semibold">{it.sku}</td>
                  <td className="max-w-xs truncate"><input className="input text-sm" value={it.name} onChange={(e) => updateField(i, "name", e.target.value)} /></td>
                  <td className="text-xs">{it.type}</td>
                  <td><input type="number" min="1" className="input w-20 text-center" value={it.qty} onChange={(e) => updateField(i, "qty", e.target.value)} /></td>
                  <td><input type="number" step="0.01" className="input w-24 text-right font-mono" value={it.unit_price} onChange={(e) => updateField(i, "unit_price", e.target.value)} /></td>
                  <td className="text-right font-mono font-semibold">{fmtBRL((parseInt(it.qty) || 0) * (parseFloat(it.unit_price) || 0))}</td>
                  <td className="text-center font-mono font-bold text-[#E60000]">{(parseInt(it.qty) || 0) * (it.units_per_unit || 1)}</td>
                  <td>
                    <select
                      className="input text-xs w-32"
                      value={it.silk_color || ""}
                      onChange={(e) => updateField(i, "silk_color", e.target.value)}
                      data-testid={`ed-silk-${i}`}
                    >
                      <option value="">— usar padrão —</option>
                      {(silkOptions || []).map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
                    </select>
                  </td>
                  <td><button onClick={() => removeItem(i)} className="text-red-500"><Trash size={14} /></button></td>
                </tr>
              ))}
              {items.length > 0 && (
                <tr className="bg-black text-white">
                  <td colSpan={5} className="text-right uppercase font-bold tracking-widest text-xs">TOTAL</td>
                  <td className="text-right font-mono font-bold">{fmtBRL(totalValue)}</td>
                  <td className="text-center font-mono font-bold text-[#FFC000]">{totalCups}</td>
                  <td></td>
                  <td></td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {error && <div className="border border-[#E60000] bg-red-50 text-[#E60000] text-sm px-3 py-2 mb-3">{error}</div>}

        <div className="flex gap-3">
          <button type="button" className="btn btn-ghost flex-1" onClick={onClose}>Cancelar</button>
          <button type="button" className="btn flex-1" onClick={save} disabled={saving || items.length === 0} data-testid="ed-save"><CheckCircle size={16} weight="bold" /> {saving ? "Salvando..." : "Salvar Itens"}</button>
        </div>
      </div>
    </div>
  );
}
